import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { BaseSceneSwapBridge } from 'js/src/libs/scene-swapping/BaseBridge';
import { updateSceneGroupDurationToEqualVoiceDrivenAssetDuration } from 'js/src/luminary';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary/luminary-selectors';

/**
 * We want to provide the ability for a user to swap from a common scene to a talking head scene.
 * The use case for this: a user creates a common SceneGroup, then adds a piece of media to the
 * SceneGroup which has a voiceover in it. In this case, we want to allow the user to swap the scene
 * design into a talking head scene design, so that they can use the transcript-related features.
 */
export class CommonToTalkingHeadBridge extends BaseSceneSwapBridge {
    override _from = VideoData.CATEGORY_COMMON;
    override _to = VideoData.CATEGORY_TALKING_HEAD;
    override _copyStickers = false;

    // this swap will change something at the SceneGroup level
    override _swapWillAlterSceneGroup = true;

    _getMediaAssetsWithVoiceover(scene: Scene) {
        return scene
            .getAssetsByType(VideoData.MEDIA_ASSET_TYPE, { deep: false })
            .filter((x) => x.isEditable && x.isVideo && x.hasVoiceover);
    }

    override _isSwapAllowedFor(originalScene: Scene): boolean {
        // you can swap a common scene to a talking head scene
        // if it contains a media asset which has_voiceover = true
        const hasMediaAssetWithVoiceover =
            this._getMediaAssetsWithVoiceover(originalScene).length > 0;

        // and the scene must be currently used as an ordinary
        // scene group (aka not a voice-driven scene group)
        const isVoiceDriven = isVoiceDrivenSceneGroup(originalScene.sceneGroup);

        return hasMediaAssetWithVoiceover && !isVoiceDriven;
    }

    override doPostSwapTransformation(originalScene: Scene, targetScene: Scene): void {
        const mediaAsset = targetScene.sceneGroup.addMediaAsset();
        mediaAsset.sourceData = this._getMediaAssetsWithVoiceover(originalScene)[0].sourceData;
        void updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({
            sceneGroup: targetScene.sceneGroup,
            waitForLuminary: false,
        });
    }
}
